// Review state machine + gating/visibility (AMB-360 / B1+A4, module 2).
//
// The single source of truth for the review back half: which transitions are legal, who may
// approve, and which items fall in a member's "my" slice. Pure + side-effect-free so it can
// gate UI (hide the Approve button), filter lists (the my/all views), and validate writes.
//
// State vocabulary matches the stored frontmatter `reviewState`:
//   none = Draft · in-review · changes-requested · approved · rejected (legacy).
// Withdraw returns an in-review/changes-requested item to Draft (none).

export type ReviewState = "none" | "in-review" | "changes-requested" | "approved" | "rejected";
export type ReviewAction = "send" | "resubmit" | "approve" | "request-changes" | "reject" | "withdraw";

// Legal transitions only. Anything not listed is illegal and yields null.
const TRANSITIONS: Record<ReviewState, Partial<Record<ReviewAction, ReviewState>>> = {
  none: { send: "in-review" },
  "in-review": {
    approve: "approved",
    "request-changes": "changes-requested",
    reject: "rejected",
    withdraw: "none",
  },
  "changes-requested": {
    send: "in-review",
    resubmit: "in-review",
    withdraw: "none",
  },
  rejected: {
    send: "in-review",
    resubmit: "in-review",
  },
  approved: {}, // terminal — promotion (C) is a separate, optional branch, not a state change
};

/** Apply an action to a state. Returns the next state, or null if the transition is illegal. */
export function reviewTransition(state: ReviewState, action: ReviewAction): ReviewState | null {
  return TRANSITIONS[state]?.[action] ?? null;
}

/** A deliverable's reviewer field may be a single id, an array, or absent. */
interface Reviewable {
  reviewState?: string | null;
  reviewer?: string | string[] | null;
  submittedBy?: string | null;
  routedTo?: string | string[] | null; // who a changes-requested item was sent back to (B3) — one or many (AMB-390)
}

/** Coerce the reviewer field to a clean id list (multi-reviewer support). */
export function normalizeReviewers(reviewer: string | string[] | null | undefined): string[] {
  if (Array.isArray(reviewer)) return reviewer.filter(Boolean);
  return reviewer ? [reviewer] : [];
}

/**
 * Hard approve-gate: only an assigned reviewer can approve, and only while in review.
 * Any one of several assigned reviewers may approve (first approval completes it). The
 * submitter is never an assignee of their own item, so they can't approve it.
 */
export function canApprove(d: Reviewable, memberId: string | null | undefined): boolean {
  if (!memberId) return false;
  if (d.reviewState !== "in-review") return false;
  return normalizeReviewers(d.reviewer).includes(memberId);
}

/** My slice: I'm one of the reviewers, I'm the submitter, or it was routed back to me. */
export function isMine(d: Reviewable, memberId: string | null | undefined): boolean {
  if (!memberId) return false;
  return (
    normalizeReviewers(d.reviewer).includes(memberId) ||
    d.submittedBy === memberId ||
    normalizeReviewers(d.routedTo).includes(memberId)
  );
}

/**
 * Visibility filter. "all" = see everything (you can look but only act on what's yours);
 * "mine" = only my slice (items I review or submitted).
 */
export function visibleTo(d: Reviewable, memberId: string | null | undefined, scope: "all" | "mine"): boolean {
  return scope === "all" ? true : isMine(d, memberId);
}
